// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import type { StepComponentProps } from '../../types/wizard';
import { FileUploadField } from '../shared/FileUploadField';
import { toIsoDate } from '../../utils/date';

/** Strip everything except digits and dot, keep only first dot */
function toRawNumber(display: string): string {
  const stripped = display.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

/** Format a raw numeric string as 1,234.56 for display (without € symbol) */
function formatEuro(raw: string): string {
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const dec = parts.length > 1 ? '.' + parts[1] : '';
  return `${formatted}${dec}`;
}

/** On blur, ensure 2 decimal places */
function formatEuroBlur(raw: string): string {
  if (!raw) return '';
  const num = parseFloat(raw);
  if (isNaN(num)) return '';
  return num.toFixed(2);
}

/** Strip non-numeric chars for percentage, keep single dot */
function toRawPercent(display: string): string {
  const stripped = display.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

const YES_NO_OPTIONS = [
  { value: 'Yes', text: 'Yes' },
  { value: 'No', text: 'No' },
];

const CRYSTALLISED_PROVIDER_OPTIONS = [
  { value: 'Irish Life', text: 'Irish Life' },
  { value: 'Zurich', text: 'Zurich' },
  { value: 'Aviva', text: 'Aviva' },
  { value: 'New Ireland', text: 'New Ireland' },
  { value: 'Standard Life', text: 'Standard Life' },
  { value: 'Royal London', text: 'Royal London' },
  { value: 'Canada Life', text: 'Canada Life' },
  { value: 'Other', text: 'Other' },
];

const DRAWDOWN_FREQUENCY_OPTIONS = [
  { value: 'Monthly', text: 'Monthly' },
  { value: 'Quarterly', text: 'Quarterly' },
  { value: 'Semi-Annual', text: 'Semi-Annual' },
  { value: 'Annual', text: 'Annual' },
  { value: 'Ad Hoc', text: 'Ad Hoc' },
];

interface TaxFreeLumpSumEntry {
  amount?: string;
  taxPaid?: string;
  date?: string;
}

const crystallisedSchema = yup.object({
  crystallisedStatus: yup.string().required('This field is required'),
  crystallisedBceDate: yup.string().required('Date of BCE is required'),
  crystallisedBceValue: yup.string().trim().required('Value of policy as BCE is required'),
  crystallisedProviderName: yup.string().required('Provider name is required'),
  crystallisedQfmName: yup.string().trim().required('QFM name is required'),
  crystallisedTaxFreeLumpSum: yup.string().trim().required('Tax Free Lump Sum is required'),
  crystallisedTaxFreeLumpSumDate: yup.string().required('Date of Tax Free is required'),
  crystallisedOtherLumpSum: yup.string().trim().required('Other Lump Sum is required'),
  crystallisedTaxPaidOtherLumpSum: yup.string().trim().required('Tax Paid On Other Lump Sum is required'),
  crystallisedOtherLumpSumDate: yup.string().required('Date of Lump Sum is required'),
  crystallisedDrawingIncome: yup.string().required('This field is required'),
  crystallisedAnnualDrawdownPercent: yup.string().trim().required('Annual drawdown % is required'),
  crystallisedLastAnnualDrawdown: yup.string().trim().required('Last annual drawdown is required'),
  crystallisedDrawdownFrequency: yup.string().required('Drawdown frequency is required'),
  crystallisedDrawdownDate: yup.string().required('Date of drawdown payment is required'),
});

function validateField(fieldName: string, data: Record<string, unknown>): string | undefined {
  try {
    crystallisedSchema.validateSyncAt(fieldName, data);
    return undefined;
  } catch (err) {
    if (err instanceof yup.ValidationError) return err.message;
    return undefined;
  }
}

export function StepCrystallisedRetired({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
  pendingFiles,
  onAddFile,
  onRemoveFile,
}: StepComponentProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
  const isCrystallised = (data.crystallisedStatus as string) === 'Yes';
  const isDrawingIncome = (data.crystallisedDrawingIncome as string) === 'Yes';

  const additionalLumpSums = useMemo(
    () => (data.crystallisedAdditionalLumpSums as TaxFreeLumpSumEntry[]) || [],
    [data.crystallisedAdditionalLumpSums],
  );

  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, data as Record<string, unknown>);
    setInlineErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [data]);

  const getError = (field: string): string | undefined => {
    return (touched[field] ? inlineErrors[field] : undefined) || fieldErrors[field];
  };

  const handleCurrencyChange = useCallback((field: string, inputValue: string) => {
    const raw = toRawNumber(inputValue);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    onFieldChange(field, raw);
    onClearFieldError(field);
  }, [onFieldChange, onClearFieldError]);

  const handleCurrencyBlur = useCallback((field: string) => {
    const raw = (data[field] as string) || '';
    if (raw) {
      onFieldChange(field, formatEuroBlur(raw));
    }
    handleBlur(field);
  }, [data, onFieldChange, handleBlur]);

  const getCurrencyDisplay = (field: string): string => {
    const raw = (data[field] as string) || '';
    return raw ? formatEuro(raw) : '';
  };

  const updateLumpSumEntry = useCallback((index: number, field: keyof TaxFreeLumpSumEntry, value: string) => {
    const updated = [...additionalLumpSums];
    updated[index] = { ...updated[index], [field]: value };
    onFieldChange('crystallisedAdditionalLumpSums', updated);
  }, [additionalLumpSums, onFieldChange]);

  const addLumpSumEntry = useCallback(() => {
    onFieldChange('crystallisedAdditionalLumpSums', [...additionalLumpSums, { amount: '', date: '' }]);
  }, [additionalLumpSums, onFieldChange]);

  const removeLumpSumEntry = useCallback((index: number) => {
    const updated = additionalLumpSums.filter((_, i) => i !== index);
    onFieldChange('crystallisedAdditionalLumpSums', updated);
  }, [additionalLumpSums, onFieldChange]);

  // Date picker SVG icon (calendar)
  const calendarIcon = (maskId: string) => (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id={maskId} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M16 0H0V16H16V0Z" fill="white"/>
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
        <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
        <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
        <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
      </g>
    </svg>
  );

  // Select chevron SVG
  const chevronIcon = (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const dateInputClass = (field: string) =>
    `w-full h-[48px] px-md pr-2xl rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data[field] as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

  const selectClass = (field: string) =>
    `w-full h-[48px] px-md pr-xxl rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data[field] as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`;

  const inputClass = (field: string) =>
    `w-full h-[48px] px-md rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`;

  const currencyWrapper = (field: string) =>
    `flex items-center w-full h-[48px] px-md rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`;

  // For additional lump sum entries - uses border color without getError
  const lumpSumDateInputClass = (hasValue: boolean) =>
    `w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${hasValue ? 'text-[#333]' : 'text-grey-04'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Crystallised / retired
      </div>

      {/* Has the policy been crystallised/retired? */}
      <div className="flex flex-col gap-sm">
        <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
          Has the policy been crystallised/retired? <span className="text-[#E7000B]">*</span>
        </div>
        <div className="relative">
          <select
            value={(data.crystallisedStatus as string) || ''}
            onChange={(e) => {
              onFieldChange('crystallisedStatus', e.target.value);
              onClearFieldError('crystallisedStatus');
            }}
            onBlur={() => handleBlur('crystallisedStatus')}
            className={selectClass('crystallisedStatus')}
          >
            <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
            {YES_NO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
            ))}
          </select>
          {chevronIcon}
        </div>
        {getError('crystallisedStatus') && (
          <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedStatus')}</div>
        )}
      </div>

      {isCrystallised && (
        <>
          {/* Row: Date of BCE* | Value of Policy as at BCE* */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Date of Benefit Crystallisation Event <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.crystallisedBceDate as string) || ''}
                  onBlur={() => {
                    handleBlur('crystallisedBceDate');
                  }}
                  onChange={(e) => {
                    onFieldChange('crystallisedBceDate', toIsoDate(e.target.value));
                    onClearFieldError('crystallisedBceDate');
                  }}
                  className={dateInputClass('crystallisedBceDate')}
                />
                {calendarIcon('mask_bce_date')}
              </div>
              {getError('crystallisedBceDate') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedBceDate')}</div>
              )}
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Value of Policy as at BCE <span className="text-[#E7000B]">*</span>
              </div>
              <div className={currencyWrapper('crystallisedBceValue')}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('crystallisedBceValue')}
                  onChange={(e) => handleCurrencyChange('crystallisedBceValue', e.target.value)}
                  onBlur={() => handleCurrencyBlur('crystallisedBceValue')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
              {getError('crystallisedBceValue') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedBceValue')}</div>
              )}
            </div>
          </div>

          {/* Row: Provider name who Crystallised* | Name of QFM* */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Provider name who Crystallised the policy <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <select
                  value={(data.crystallisedProviderName as string) || ''}
                  onChange={(e) => {
                    onFieldChange('crystallisedProviderName', e.target.value);
                    onClearFieldError('crystallisedProviderName');
                  }}
                  onBlur={() => handleBlur('crystallisedProviderName')}
                  className={selectClass('crystallisedProviderName')}
                >
                  <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                  {CRYSTALLISED_PROVIDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                  ))}
                </select>
                {chevronIcon}
              </div>
              {getError('crystallisedProviderName') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedProviderName')}</div>
              )}
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Name of Qualifying Fund Manager (QFM) <span className="text-[#E7000B]">*</span>
              </div>
              <input
                type="text"
                value={(data.crystallisedQfmName as string) || ''}
                onChange={(e) => {
                  onFieldChange('crystallisedQfmName', e.target.value);
                  onClearFieldError('crystallisedQfmName');
                }}
                onBlur={() => handleBlur('crystallisedQfmName')}
                className={inputClass('crystallisedQfmName')}
              />
              {getError('crystallisedQfmName') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedQfmName')}</div>
              )}
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="w-full h-[1px] bg-[#E1E1E1]" />

          {/* ── Lump Sums Section ── */}
          <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
            Lump Sums
          </div>

          {/* Row 1: Tax Free Lump Sum* | Date of Tax Free* */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Tax Free Lump Sum <span className="text-[#E7000B]">*</span>
              </div>
              <div className={currencyWrapper('crystallisedTaxFreeLumpSum')}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('crystallisedTaxFreeLumpSum')}
                  onChange={(e) => handleCurrencyChange('crystallisedTaxFreeLumpSum', e.target.value)}
                  onBlur={() => handleCurrencyBlur('crystallisedTaxFreeLumpSum')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
              {getError('crystallisedTaxFreeLumpSum') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedTaxFreeLumpSum')}</div>
              )}
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Date of Tax Free <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.crystallisedTaxFreeLumpSumDate as string) || ''}
                  onBlur={() => {
                    handleBlur('crystallisedTaxFreeLumpSumDate');
                  }}
                  onChange={(e) => {
                    onFieldChange('crystallisedTaxFreeLumpSumDate', toIsoDate(e.target.value));
                    onClearFieldError('crystallisedTaxFreeLumpSumDate');
                  }}
                  className={dateInputClass('crystallisedTaxFreeLumpSumDate')}
                />
                {calendarIcon('mask_tfls_date')}
              </div>
              {getError('crystallisedTaxFreeLumpSumDate') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedTaxFreeLumpSumDate')}</div>
              )}
            </div>
          </div>

          {/* Row 2: Other Lump Sum* | Tax Paid On Other Lump Sum* | Date of Lump Sum* */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Other Lump Sum <span className="text-[#E7000B]">*</span>
              </div>
              <div className={currencyWrapper('crystallisedOtherLumpSum')}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('crystallisedOtherLumpSum')}
                  onChange={(e) => handleCurrencyChange('crystallisedOtherLumpSum', e.target.value)}
                  onBlur={() => handleCurrencyBlur('crystallisedOtherLumpSum')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
              {getError('crystallisedOtherLumpSum') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedOtherLumpSum')}</div>
              )}
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Tax Paid On Other Lump Sum <span className="text-[#E7000B]">*</span>
              </div>
              <div className={currencyWrapper('crystallisedTaxPaidOtherLumpSum')}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('crystallisedTaxPaidOtherLumpSum')}
                  onChange={(e) => handleCurrencyChange('crystallisedTaxPaidOtherLumpSum', e.target.value)}
                  onBlur={() => handleCurrencyBlur('crystallisedTaxPaidOtherLumpSum')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
              {getError('crystallisedTaxPaidOtherLumpSum') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedTaxPaidOtherLumpSum')}</div>
              )}
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Date of Lump Sum <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.crystallisedOtherLumpSumDate as string) || ''}
                  onBlur={() => {
                    handleBlur('crystallisedOtherLumpSumDate');
                  }}
                  onChange={(e) => {
                    onFieldChange('crystallisedOtherLumpSumDate', toIsoDate(e.target.value));
                    onClearFieldError('crystallisedOtherLumpSumDate');
                  }}
                  className={dateInputClass('crystallisedOtherLumpSumDate')}
                />
                {calendarIcon('mask_ols_date')}
              </div>
              {getError('crystallisedOtherLumpSumDate') && (
                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedOtherLumpSumDate')}</div>
              )}
            </div>
          </div>

          {/* Additional Lump Sum entries */}
          {additionalLumpSums.map((entry, index) => (
            <div key={index} className="flex flex-col gap-md rounded-lg p-md" style={{ background: '#F8F8FF' }}>
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                    Other Lump Sum <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary">
                    <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={entry.amount ? formatEuro(entry.amount) : ''}
                      onChange={(e) => {
                        const raw = toRawNumber(e.target.value);
                        if (raw && !/^\d*\.?\d*$/.test(raw)) return;
                        updateLumpSumEntry(index, 'amount', raw);
                      }}
                      onBlur={() => {
                        if (entry.amount) updateLumpSumEntry(index, 'amount', formatEuroBlur(entry.amount));
                      }}
                      className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-sm flex-1">
                  <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                    Tax Paid On Other Lump Sum <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary">
                    <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={entry.taxPaid ? formatEuro(entry.taxPaid) : ''}
                      onChange={(e) => {
                        const raw = toRawNumber(e.target.value);
                        if (raw && !/^\d*\.?\d*$/.test(raw)) return;
                        updateLumpSumEntry(index, 'taxPaid', raw);
                      }}
                      onBlur={() => {
                        if (entry.taxPaid) updateLumpSumEntry(index, 'taxPaid', formatEuroBlur(entry.taxPaid));
                      }}
                      className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-sm flex-1">
                  <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                    Date of Lump Sum <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <input
                      type="date"
                      value={entry.date || ''}
                      onChange={(e) => updateLumpSumEntry(index, 'date', toIsoDate(e.target.value))}
                      className={lumpSumDateInputClass(!!entry.date)}
                    />
                    {calendarIcon(`mask_ls_${index}`)}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => removeLumpSumEntry(index)}
                className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath={`url(#clip_trash_${index})`}>
                    <mask id={`mask_trash_${index}`} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                      <path d="M15.5 0.5V15.5H0.5V0.5H15.5Z" fill="white" stroke="white"/>
                    </mask>
                    <g mask={`url(#mask_trash_${index})`}>
                      <path d="M14.0004 4.4873C13.9871 4.4873 13.9671 4.4873 13.9471 4.4873C10.4204 4.13397 6.90042 4.00064 3.41377 4.35397L2.05377 4.4873C1.77377 4.51397 1.5271 4.31397 1.50043 4.03397C1.47377 3.75397 1.67377 3.51397 1.9471 3.4873L3.3071 3.35397C6.85375 2.99397 10.4471 3.13397 14.0471 3.4873C14.3204 3.51397 14.5204 3.76064 14.4938 4.03397C14.4738 4.29397 14.2538 4.4873 14.0004 4.4873Z" fill="#2D0A6C"/>
                      <path d="M5.66749 3.8135C5.64082 3.8135 5.61415 3.8135 5.58082 3.80683C5.31415 3.76016 5.12749 3.50016 5.17415 3.2335L5.32082 2.36016C5.42749 1.72016 5.57415 0.833496 7.12748 0.833496H8.87415C10.4341 0.833496 10.5808 1.7535 10.6808 2.36683L10.8275 3.2335C10.8741 3.50683 10.6875 3.76683 10.4208 3.80683C10.1475 3.8535 9.88748 3.66683 9.84748 3.40016L9.70081 2.5335C9.60748 1.9535 9.58748 1.84016 8.88081 1.84016H7.13415C6.42749 1.84016 6.41415 1.9335 6.31415 2.52683L6.16082 3.3935C6.12082 3.64016 5.90749 3.8135 5.66749 3.8135Z" fill="#2D0A6C"/>
                      <path d="M10.1406 15.1667H5.86058C3.53392 15.1667 3.44058 13.8801 3.36725 12.8401L2.93392 6.12672C2.91392 5.85338 3.12725 5.61338 3.40058 5.59338C3.68058 5.58005 3.91392 5.78672 3.93392 6.06005L4.36725 12.7734C4.44058 13.7867 4.46725 14.1667 5.86058 14.1667H10.1406C11.5406 14.1667 11.5672 13.7867 11.6339 12.7734L12.0672 6.06005C12.0872 5.78672 12.3273 5.58005 12.6006 5.59338C12.8739 5.61338 13.0872 5.84672 13.0672 6.12672L12.6339 12.8401C12.5606 13.8801 12.4672 15.1667 10.1406 15.1667Z" fill="#2D0A6C"/>
                      <path d="M9.10673 11.5H6.88673C6.61339 11.5 6.38672 11.2733 6.38672 11C6.38672 10.7267 6.61339 10.5 6.88673 10.5H9.10673C9.38007 10.5 9.60673 10.7267 9.60673 11C9.60673 11.2733 9.38007 11.5 9.10673 11.5Z" fill="#141414"/>
                      <path d="M9.66732 8.8335H6.33398C6.06065 8.8335 5.83398 8.60683 5.83398 8.3335C5.83398 8.06016 6.06065 7.8335 6.33398 7.8335H9.66732C9.94065 7.8335 10.1673 8.06016 10.1673 8.3335C10.1673 8.60683 9.94065 8.8335 9.66732 8.8335Z" fill="#141414"/>
                    </g>
                  </g>
                  <defs>
                    <clipPath id={`clip_trash_${index}`}>
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
                  Remove This Lump Sum
                </div>
              </button>
            </div>
          ))}

          {/* Add Another Lump Sum button */}
          <button
            type="button"
            onClick={addLumpSumEntry}
            className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip_add)">
                <mask id="mask_add" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                  <path d="M15.5 0.5V15.5H0.5V0.5H15.5Z" fill="white" stroke="white"/>
                </mask>
                <g mask="url(#mask_add)">
                  <path d="M10.6663 8.5H5.33301C5.05967 8.5 4.83301 8.27333 4.83301 8C4.83301 7.72667 5.05967 7.5 5.33301 7.5H10.6663C10.9397 7.5 11.1663 7.72667 11.1663 8C11.1663 8.27333 10.9397 8.5 10.6663 8.5Z" fill="#2D0A6C"/>
                  <path d="M8 11.1668C7.72667 11.1668 7.5 10.9402 7.5 10.6668V5.3335C7.5 5.06016 7.72667 4.8335 8 4.8335C8.27333 4.8335 8.5 5.06016 8.5 5.3335V10.6668C8.5 10.9402 8.27333 11.1668 8 11.1668Z" fill="#2D0A6C"/>
                  <path d="M9.99967 15.1668H5.99967C2.37967 15.1668 0.833008 13.6202 0.833008 10.0002V6.00016C0.833008 2.38016 2.37967 0.833496 5.99967 0.833496H9.99967C13.6197 0.833496 15.1663 2.38016 15.1663 6.00016V10.0002C15.1663 13.6202 13.6197 15.1668 9.99967 15.1668ZM5.99967 1.8335C2.92634 1.8335 1.83301 2.92683 1.83301 6.00016V10.0002C1.83301 13.0735 2.92634 14.1668 5.99967 14.1668H9.99967C13.073 14.1668 14.1663 13.0735 14.1663 10.0002V6.00016C14.1663 2.92683 13.073 1.8335 9.99967 1.8335H5.99967Z" fill="#2D0A6C"/>
                </g>
              </g>
              <defs>
                <clipPath id="clip_add">
                  <rect width="16" height="16" fill="white"/>
                </clipPath>
              </defs>
            </svg>
            <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
              Add Another Lump Sum
            </div>
          </button>

          {/* Upload Documents */}
          <FileUploadField
            fieldKey="crystallisedDocumentUpload"
            files={pendingFiles?.get('crystallisedDocumentUpload')}
            onAddFile={onAddFile}
            onRemoveFile={onRemoveFile}
          />

          {/* ── Divider ── */}
          <div className="w-full h-[1px] bg-[#E1E1E1]" />

          {/* ── Income drawn from policy Section ── */}
          <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
            Income drawn from policy
          </div>

          {/* Is the Policyholder drawing income from the policy? */}
          <div className="flex flex-col gap-sm">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              Is the Policyholder drawing income from the policy? <span className="text-[#E7000B]">*</span>
            </div>
            <div className="relative">
              <select
                value={(data.crystallisedDrawingIncome as string) || ''}
                onChange={(e) => {
                  onFieldChange('crystallisedDrawingIncome', e.target.value);
                  onClearFieldError('crystallisedDrawingIncome');
                }}
                onBlur={() => handleBlur('crystallisedDrawingIncome')}
                className={selectClass('crystallisedDrawingIncome')}
              >
                <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                {YES_NO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                ))}
              </select>
              {chevronIcon}
            </div>
            {getError('crystallisedDrawingIncome') && (
              <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedDrawingIncome')}</div>
            )}
          </div>

          {isDrawingIncome && (
            <>
              {/* Row: Annual Drawdown %* | Last Annual Drawdown* */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                    Annual Drawdown % <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${getError('crystallisedAnnualDrawdownPercent') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={(data.crystallisedAnnualDrawdownPercent as string) || ''}
                      onChange={(e) => {
                        const raw = toRawPercent(e.target.value);
                        if (raw && !/^\d*\.?\d*$/.test(raw)) return;
                        onFieldChange('crystallisedAnnualDrawdownPercent', raw);
                        onClearFieldError('crystallisedAnnualDrawdownPercent');
                      }}
                      onBlur={() => handleBlur('crystallisedAnnualDrawdownPercent')}
                      className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                    />
                    <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none" style={{ marginLeft: '8px' }}>%</span>
                  </div>
                  {getError('crystallisedAnnualDrawdownPercent') && (
                    <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedAnnualDrawdownPercent')}</div>
                  )}
                </div>

                <div className="flex flex-col gap-sm flex-1">
                  <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                    Last Annual Drawdown <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className={currencyWrapper('crystallisedLastAnnualDrawdown')}>
                    <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={getCurrencyDisplay('crystallisedLastAnnualDrawdown')}
                      onChange={(e) => handleCurrencyChange('crystallisedLastAnnualDrawdown', e.target.value)}
                      onBlur={() => handleCurrencyBlur('crystallisedLastAnnualDrawdown')}
                      className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                    />
                  </div>
                  {getError('crystallisedLastAnnualDrawdown') && (
                    <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedLastAnnualDrawdown')}</div>
                  )}
                </div>
              </div>

              {/* Row: Drawdown Payment Frequency* | Date of Drawdown Payment* */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                    Drawdown Payment Frequency <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <select
                      value={(data.crystallisedDrawdownFrequency as string) || ''}
                      onChange={(e) => {
                        onFieldChange('crystallisedDrawdownFrequency', e.target.value);
                        onClearFieldError('crystallisedDrawdownFrequency');
                      }}
                      onBlur={() => handleBlur('crystallisedDrawdownFrequency')}
                      className={selectClass('crystallisedDrawdownFrequency')}
                    >
                      <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                      {DRAWDOWN_FREQUENCY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                      ))}
                    </select>
                    {chevronIcon}
                  </div>
                  {getError('crystallisedDrawdownFrequency') && (
                    <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedDrawdownFrequency')}</div>
                  )}
                </div>

                <div className="flex flex-col gap-sm flex-1">
                  <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                    Date of Drawdown Payment <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <input
                      type="date"
                      value={(data.crystallisedDrawdownDate as string) || ''}
                      onBlur={() => {
                        handleBlur('crystallisedDrawdownDate');
                      }}
                      onChange={(e) => {
                        onFieldChange('crystallisedDrawdownDate', toIsoDate(e.target.value));
                        onClearFieldError('crystallisedDrawdownDate');
                      }}
                      className={dateInputClass('crystallisedDrawdownDate')}
                    />
                    {calendarIcon('mask_dd_date')}
                  </div>
                  {getError('crystallisedDrawdownDate') && (
                    <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('crystallisedDrawdownDate')}</div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
